import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faDollarSign, faCreditCard } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

// Agregar íconos a la librería
library.add(faDollarSign, faCreditCard);
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import IconButton from 'components/common/IconButton';
import ModalConfirmacionPagoSuscripcion from './ModalConfirmacionPagoSuscripcion';

/**
 * ModalNuevoUsuarioAkasiaPOS
 * Modal visual para crear un nuevo usuario Akasia POS (solo UI)
 * Integrado con lógica de suscripciones (26 Dic 2025)
 */
const ModalNuevoUsuarioAkasiaPOS = ({ show, onHide, idLicencia: initialIdLicencia, licencias, onCreated, infoSuscripcionProp }) => {
    const [saving, setSaving] = useState(false);
    const [productos, setProductos] = useState([]);
    const [productosFiltrados, setProductosFiltrados] = useState([]); // Productos filtrados por tipo de suscripción
    const [renovaciones, setRenovaciones] = useState([]);
    const [renovacionesFiltradas, setRenovacionesFiltradas] = useState([]); // Renovaciones filtradas
    const [formasPago, setFormasPago] = useState([]);
    const [productoSeleccionado, setProductoSeleccionado] = useState(null);
    const [licensesList, setLicensesList] = useState([]);
    const [selectedLicense, setSelectedLicense] = useState(initialIdLicencia || 0);
    const [selectedFormaPago, setSelectedFormaPago] = useState(null);
    const [vMoneda, setVMoneda] = useState('MXN');
    const [dTipoCambio, setDTipoCambio] = useState(1.0);
    const [consultandoTC, setConsultandoTC] = useState(false);

    // Estados para sucursales (27 Dic 2024)
    const [sucursales, setSucursales] = useState([]);
    const [selectedSucursal, setSelectedSucursal] = useState(null);
    const [cargandoSucursales, setCargandoSucursales] = useState(false);

    // Estados para roles (05 Feb 2026)
    const [roles, setRoles] = useState([]);
    const [selectedRol, setSelectedRol] = useState(null);
    const [cargandoRoles, setCargandoRoles] = useState(false);

    // Estados para configuración personalizable (todos los productos son editables)
    const [tipoRenovacionSeleccionado, setTipoRenovacionSeleccionado] = useState('');
    const [precioTotal, setPrecioTotal] = useState('');
    const [precioUnitario, setPrecioUnitario] = useState(0);
    const [ivaCalculado, setIvaCalculado] = useState(0);

    // Estados para manejo de suscripción
    const [infoSuscripcion, setInfoSuscripcion] = useState(null);
    const [showModalConfirmacion, setShowModalConfirmacion] = useState(false);
    const [payloadPendiente, setPayloadPendiente] = useState(null);

    const [values, setValues] = useState({
        vUsuario: '',
        vContrasena: '',
        vEmail: '',
        vNombre: '',
        vApellidos: '',
        vTelefono: '',
        vDireccion: ''
    });

    const { token, setToken } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setValues({ ...values, [name]: type === 'checkbox' ? checked : value });
    };

    // Calcular IVA y precio unitario cuando cambia el precio total
    const recalcularPrecios = (total) => {
        const totalNum = parseFloat(total) || 0;
        // Total = Subtotal + IVA
        // Total = Subtotal + (Subtotal * 0.16)
        // Total = Subtotal * 1.16
        // Subtotal = Total / 1.16
        const subtotal = totalNum / 1.16;
        const iva = totalNum - subtotal;

        setPrecioUnitario(subtotal);
        setIvaCalculado(iva);
    };

    // Generar cadena aleatoria para usuario o contraseña
    const generarCadenaRandom = async (campo) => {
        try {
            const response = await fetch('/api/licencia/ObtenerCadenaRandom', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'text/plain'
                }
            });

            if (response.ok) {
                const data = await response.text();
                if (campo === 'vUsuario') {
                    setValues({ ...values, vUsuario: data });
                } else if (campo === 'vContrasena') {
                    setValues({ ...values, vContrasena: data });
                }
            } else {
                showErrorToast('Error al generar cadena aleatoria');
            }
        } catch (error) {
            showErrorToast('Error al generar cadena: ' + error.message);
        }
    };

    // Obtener tipo de cambio de Banxico para USD
    const obtenerBanxicoTC = async () => {
        const date_ = new Date().toLocaleString('sv', { timeZoneName: 'short' }).substring(0, 10);
        setConsultandoTC(true);

        try {
            const response = await fetch(
                `https://www.banxico.org.mx/SieAPIRest/service/v1/series/SF60653/datos/${date_}/${date_}?decimales=sinCeros&token=7d0140dc6159dbe4a870c61dce8136190da8e9757776cb679483bdaaa31643e6`,
                { method: 'GET' }
            );

            if (response.ok) {
                const data = await response.json();
                const tipoCambio = parseFloat(data.bmx.series[0].datos[0].dato);
                setDTipoCambio(tipoCambio);
            } else {
                showErrorToast('Error al consultar tipo de cambio');
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
                // Fallback
                alert("Hubo un problema con la conexión al Banco de México, por lo que estableceremos el Tipo de Cambio a 19.555.");
                setDTipoCambio(19.555);
            }
        } catch (error) {
            showErrorToast('Error al consultar tipo de cambio: ' + error.message);
            // Fallback
            alert("Hubo un problema con la conexión al Banco de México, por lo que estableceremos el Tipo de Cambio a 19.555.");
            setDTipoCambio(19.555);
        } finally {
            setConsultandoTC(false);
        }
    };

    // Obtener roles de la empresa (05 Feb 2026)
    const obtenerRolesEmpresa = async (vUUIDEmpresaParam, idLicenciaParam) => {
        if (!vUUIDEmpresaParam || !idLicenciaParam) {
            console.warn('⚠️ obtenerRolesEmpresa: Faltan parámetros', { vUUIDEmpresaParam, idLicenciaParam });
            return;
        }

        setCargandoRoles(true);
        try {
            const response = await fetch(`/api/UsuariosAkasiaPOS/ObtenerRolesEmpresa?vUUIDEmpresa=${vUUIDEmpresaParam}&idLicencia=${idLicenciaParam}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('🔧 Respuesta roles:', data);

            if (data.iResponseType === 1 && data.detalles) {
                // El controller retorna data.detalles como JSON string, necesita parsing
                const rolesArray = JSON.parse(data.detalles);
                setRoles(rolesArray);
                console.log(`✅ ${rolesArray.length} roles cargados`, rolesArray);

                // Seleccionar primer rol por defecto si existe
                if (rolesArray.length > 0) {
                    setSelectedRol(rolesArray[0].iRolId);
                }
            } else {
                console.warn('⚠️ No se encontraron roles:', data.vMessage);
                setRoles([]);
            }
        } catch (error) {
            console.error('❌ Error al cargar roles:', error);
            showErrorToast('Error al cargar roles de la empresa');
            setRoles([]);
        } finally {
            setCargandoRoles(false);
        }
    };

    // Obtener sucursales de la empresa (27 Dic 2024)
    const obtenerSucursalesEmpresa = async (vUUIDEmpresaParam, idLicenciaParam) => {
        if (!vUUIDEmpresaParam) {
            return;
        }

        if (!idLicenciaParam) {
            return;
        }

        setCargandoSucursales(true);
        try {
            const response = await fetch(`/api/UsuariosAkasiaPOS/ObtenerSucursalesEmpresa?vUUIDEmpresa=${vUUIDEmpresaParam}&idLicencia=${idLicenciaParam}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1 && data.detalles) {
                    // data.detalles viene como string JSON, parsearlo
                    const sucursalesData = JSON.parse(data.detalles);

                    setSucursales(sucursalesData);
                    console.log(`✅ ${sucursalesData.length} sucursales cargadas`);

                    // Auto-seleccionar primera sucursal si solo hay una
                    if (sucursalesData.length === 1) {
                        setSelectedSucursal(sucursalesData[0].idSucursal);
                    }
                } else {
                    showErrorToast(data.vMessage || 'No se pudieron obtener las sucursales');
                    setSucursales([]);
                }
            } else {
                showErrorToast(`Error ${response.status} al obtener sucursales`);
                setSucursales([]);
            }
        } catch (error) {
            showErrorToast('Error al obtener sucursales: ' + error.message);
            setSucursales([]);
        } finally {
            setCargandoSucursales(false);
        }
    };

    // Cargar datos iniciales desde el endpoint combinado getDataRegisterUserPOS
    const obtenerDatosIniciales = async (idLicenciaToUse) => {
        try {
            const response = await fetch(`/api/UsuariosAkasiaPOS/getDataRegisterUserPOS?idLicencia=${idLicenciaToUse}`, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1 && data.detalles) {
                    const parsed = JSON.parse(data.detalles);
                    // usamos nombres de tabla explícitos: licencia, productos, renovaciones, formasPago
                    setLicensesList(parsed.licencia || []);
                    setProductos(parsed.productos || []);
                    setRenovaciones(parsed.renovaciones || []);
                    // formasPago: filtrar según si hay suscripción
                    const todasFormasPago = parsed.formasPago || [];

                    // Si hay suscripción, permitir todas las formas de pago
                    // Si NO hay suscripción, filtrar la forma "99 - POR DEFINIR" ya que es solo para suscripciones
                    let formasPagoFiltradas;
                    if (infoSuscripcionProp) {
                        // Con suscripción: mostrar todas, preferir la forma de pago de la suscripción
                        formasPagoFiltradas = todasFormasPago;
                        setSelectedFormaPago(infoSuscripcionProp.idCatFormaPago || (todasFormasPago[0]?.idCatFormaPago));
                    } else {
                        // Sin suscripción: filtrar la clave '99' (POR DEFINIR) ya que es solo para suscripciones
                        formasPagoFiltradas = todasFormasPago.filter(f => String(f.vClave).trim() !== '99');
                        if (formasPagoFiltradas.length > 0) {
                            setSelectedFormaPago(formasPagoFiltradas[0].idCatFormaPago);
                        }
                    }

                    setFormasPago(formasPagoFiltradas);
                    if (!selectedLicense && Array.isArray(parsed.licencia) && parsed.licencia.length > 0) {
                        setSelectedLicense(parsed.licencia[0].idLicencia);
                    }
                } else {
                    showErrorToast(data.vMessage || 'No se pudieron obtener datos iniciales');
                }
            } else {
                showErrorToast(response.status + ' ' + response.statusText);
            }
        } catch (err) {
            showErrorToast('Error al obtener datos iniciales');
        }
    };

    // Filtrar productos según tipo de renovación de suscripción
    const filtrarProductosPorSuscripcion = async (productosOriginales, tipoRenovacionSuscripcion) => {
        if (!tipoRenovacionSuscripcion) {
            // Sin suscripción, mostrar todos los productos
            return productosOriginales;
        }


        const productosFiltrados = [];

        // Verificar cada producto para ver si tiene renovaciones compatibles
        for (const producto of productosOriginales) {
            try {
                const response = await fetch(`/api/Suscripciones/GetRenovacionesByProducto?idProducto=${producto.idProducto}`, {
                    method: 'GET',
                    headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.iResponseType === 1) {
                        const parsed = JSON.parse(data.detalles);
                        const renovaciones = parsed.table2 || [];

                        // Verificar si tiene al menos una renovación del mismo tipo que la suscripción
                        const tieneRenovacionCompatible = renovaciones.some(
                            r => (r.vTipoRenovacion || r.vNombreRenovacion || '').toLowerCase() === tipoRenovacionSuscripcion.toLowerCase()
                        );

                        if (tieneRenovacionCompatible) {
                            productosFiltrados.push(producto);
                        }
                    }
                }
            } catch {
                // Ignore verification errors
            }
        }

        return productosFiltrados;
    };

    // Información de suscripción recibida como prop desde el componente padre

    // NOTE: licencia loading is handled by obtenerDatosIniciales which calls the unified endpoint

    // Efecto para filtrar productos cuando se cargan o cambia la suscripción
    useEffect(() => {
        const aplicarFiltros = async () => {
            if (productos.length > 0 && infoSuscripcion && infoSuscripcion.vTipoRenovacion) {
                console.log('🔄 Aplicando filtros de suscripción a productos...');
                const filtrados = await filtrarProductosPorSuscripcion(productos, infoSuscripcion.vTipoRenovacion);
                setProductosFiltrados(filtrados);
                console.log(`✅ ${filtrados.length} productos compatibles con suscripción ${infoSuscripcion.vTipoRenovacion}`);
            } else if (productos.length > 0) {
                // Sin suscripción, usar todos los productos
                setProductosFiltrados(productos);
            }
        };

        aplicarFiltros();
    }, [productos, infoSuscripcion]);

    useEffect(() => {
        if (show) {
            // Prefer passing idLicencia (already available in detallesLicencia) to obtain the specific license
            const idLic = initialIdLicencia || (licencias && licencias.idLicencia);
            if (idLic) {
                obtenerDatosIniciales(idLic);
                setSelectedLicense(idLic);
            }

            // Cargar sucursales de la empresa usando vUUIDEmpresa (27 Dic 2024)
            if (licencias && licencias.vUUIDEmpresa && idLic) {
                obtenerSucursalesEmpresa(licencias.vUUIDEmpresa, idLic);
                // Cargar roles también (05 Feb 2026)
                console.log('🔍 Cargando roles para UUID empresa:', licencias.vUUIDEmpresa, 'licencia:', idLic);
                obtenerRolesEmpresa(licencias.vUUIDEmpresa, idLic);
            } else {
                console.warn('No se encontró vUUIDEmpresa o idLicencia en licencias');
            }

            // Usar la información de suscripción pasada como prop
            if (infoSuscripcionProp) {
                console.log('✅ Usando info de suscripción desde prop:', infoSuscripcionProp);
                setInfoSuscripcion(infoSuscripcionProp);

                // Preseleccionar el tipo de renovación de la suscripción
                if (infoSuscripcionProp.vTipoRenovacion) {
                    setTipoRenovacionSeleccionado(infoSuscripcionProp.vTipoRenovacion);
                }

                // 🔒 USAR AUTOMÁTICAMENTE LA MONEDA DE LA SUSCRIPCIÓN
                // eslint-disable-next-line react/prop-types
                if (infoSuscripcionProp.vMoneda) {
                    // eslint-disable-next-line react/prop-types
                    setVMoneda(infoSuscripcionProp.vMoneda);
                    // eslint-disable-next-line react/prop-types
                    console.log('🔒 Moneda bloqueada por suscripción:', infoSuscripcionProp.vMoneda);

                    // Si es USD, obtener tipo de cambio
                    // eslint-disable-next-line react/prop-types
                    if (infoSuscripcionProp.vMoneda === 'USD') {
                        obtenerBanxicoTC();
                    }
                }

                // 🔒 USAR AUTOMÁTICAMENTE LA FORMA DE PAGO DE LA SUSCRIPCIÓN
                if (infoSuscripcionProp.idCatFormaPago) {
                    setSelectedFormaPago(infoSuscripcionProp.idCatFormaPago);
                    console.log('🔒 Forma de pago bloqueada por suscripción:', infoSuscripcionProp.idCatFormaPago);
                }

                // Filtrar formas de pago para incluir la clave '99' solo cuando hay suscripción
                setFormasPago(prevFormas => {
                    // Si había formas previamente cargadas y ahora hay suscripción, mostrar todas
                    return prevFormas; // Ya se filtraron correctamente en obtenerDatosIniciales
                });
            } else {
                console.log('ℹ️ No hay suscripción activa (prop vacío)');
                setInfoSuscripcion(null);

                // Si no hay suscripción, asegurar que la forma de pago '99' no esté disponible
                setFormasPago(prevFormas => {
                    const formasFiltradas = prevFormas.filter(f => String(f.vClave).trim() !== '99');
                    if (formasFiltradas.length > 0 && !formasFiltradas.some(f => f.idCatFormaPago === selectedFormaPago)) {
                        setSelectedFormaPago(formasFiltradas[0].idCatFormaPago);
                    }
                    return formasFiltradas;
                });
            }
        } else {
            // limpiar estado al cerrar
            setProductoSeleccionado(null);
            setRenovaciones([]);
            setRenovacionesFiltradas([]);
            setProductosFiltrados([]);
            setValues({ vUsuario: '', vContrasena: '', vEmail: '' });
            setInfoSuscripcion(null);
            setPayloadPendiente(null);
            setTipoRenovacionSeleccionado('');
            setPrecioTotal('');
            setPrecioUnitario(0);
            setIvaCalculado(0);
            setVMoneda('MXN'); // Restablecer moneda por defecto
            setDTipoCambio(1.0); // Restablecer tipo de cambio
            // Limpiar sucursales (27 Dic 2024)
            setSucursales([]);
            setSelectedSucursal(null);
        }
    }, [show, infoSuscripcionProp, licencias]);

    const onSelectProducto = async (prod) => {
        setProductoSeleccionado(prod);
        setRenovaciones([]);
        setRenovacionesFiltradas([]);

        // Usar siempre la moneda del producto seleccionado
        const moneda = prod.vMoneda || 'MXN';
        setVMoneda(moneda);
        console.log(`💰 Moneda del producto seleccionado: ${moneda}`);

        if (moneda === 'USD') {
            await obtenerBanxicoTC();
        } else {
            setDTipoCambio(1.0);
        }

        try {
            const response = await fetch(`/api/Suscripciones/GetRenovacionesByProducto?idProducto=${prod.idProducto}`, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                if (data.iResponseType === 1) {
                    const parsed = JSON.parse(data.detalles);
                    const renos = parsed.table2 || [];
                    setRenovaciones(renos);
                    setRenovacionesFiltradas(renos); // Mostrar todas las renovaciones

                    console.log(`✅ ${renos.length} renovaciones cargadas`);

                    // Auto-prellenar con la primera renovación si existe
                    if (renos.length > 0) {
                        const primerRenovacion = renos[0];
                        setPrecioTotal(primerRenovacion.dTotal ? primerRenovacion.dTotal.toString() : '0.00');
                        setPrecioUnitario(primerRenovacion.dPrecioUnitario || 0);
                        setIvaCalculado(primerRenovacion.dIVA || 0);
                        setTipoRenovacionSeleccionado(primerRenovacion.vTipoRenovacion || primerRenovacion.vNombreRenovacion || '');

                        console.log('✅ Auto-prellenado con primera renovación:', primerRenovacion);
                    }
                } else {
                    showErrorToast(data.vMessage || 'No se pudieron obtener renovaciones');
                }
            } else {
                showErrorToast('Error obteniendo renovaciones del producto');
            }
        } catch (error) {
            console.error('Error obteniendo renovaciones:', error);
            showErrorToast('Error de conexión obteniendo renovaciones');
        }
    };

    const handleSave = async () => {
        // Validaciones básicas
        if (!selectedLicense) return showErrorToast('Seleccione una licencia');
        if (!selectedSucursal) return showErrorToast('Seleccione una sucursal');
        if (!selectedRol) return showErrorToast('Seleccione un rol'); // NUEVO (05 Feb 2026)
        if (!selectedFormaPago) return showErrorToast('Seleccione una forma de pago');
        if (!productoSeleccionado) return showErrorToast('Seleccione un producto');

        // Validaciones para configuración de precios
        if (!tipoRenovacionSeleccionado) return showErrorToast('Seleccione tipo de renovación');
        if (!precioTotal || parseFloat(precioTotal) <= 0) {
            return showErrorToast('Ingrese un precio total válido');
        }

        if (!values.vNombre) return showErrorToast('Nombre es obligatorio');
        if (!values.vApellidos) return showErrorToast('Apellidos es obligatorio');
        if (!values.vUsuario) return showErrorToast('Usuario es obligatorio');
        if (!values.vEmail) return showErrorToast('Correo es obligatorio');
        if (!values.vContrasena) return showErrorToast('Contraseña es obligatoria');

        // Validar que email y contraseña no tengan espacios
        if (/\s/.test(values.vContrasena)) return showErrorToast('La contraseña no debe contener espacios');
        if (/\s/.test(values.vEmail)) return showErrorToast('El correo no debe contener espacios');

        // Validación básica de formato de correo (x@x.x)
        const emailRe = /^\S+@\S+\.\S+$/;
        if (!emailRe.test(values.vEmail)) return showErrorToast('Correo inválido. Formato mínimo: x@x.x');

        // Determinar flujo según forma de pago y existencia de suscripción
        const formaPagoSeleccionada = formasPago.find(f => f.idCatFormaPago === selectedFormaPago);
        const esSuscripcion = formaPagoSeleccionada && String(formaPagoSeleccionada.vClave).trim() == '99';

        // Buscar la renovación seleccionada o usar datos editados
        let renovacionEspecifica = null;
        if (renovaciones.length > 0) {
            renovacionEspecifica = renovaciones.find(r =>
                (r.vTipoRenovacion || r.vNombreRenovacion) === tipoRenovacionSeleccionado
            );
        }

        // Preparar datos de renovación con valores editables
        const datosRenovacion = {
            idProductoRenovaciones: renovacionEspecifica?.idProductoRenovaciones || renovacionEspecifica?.idProductoRenovacion || null,
            vTipoRenovacion: tipoRenovacionSeleccionado,
            dPrecioUnitario: precioUnitario,
            dSubTotal: precioUnitario,
            dIVA: ivaCalculado,
            dTotal: parseFloat(precioTotal)
        };

        // Preparar payload base
        const payload = {
            vClave: formaPagoSeleccionada.vClave,
            idLicencia: selectedLicense,
            idSucursal: selectedSucursal,
            bIsUsuarioPagado: 1, // AdminPanel siempre crea usuarios de pago
            idProducto: productoSeleccionado.idProducto,
            vNombreProductoPersonalizado: null, // Ya no hay productos personalizados
            idProductoRenovaciones: datosRenovacion.idProductoRenovaciones,
            vTipoRenovacion: datosRenovacion.vTipoRenovacion,
            idCatFormaPago: selectedFormaPago,
            vUsername: values.vUsuario,
            vEmail: values.vEmail,
            vPassword: values.vContrasena,
            vNombre: values.vNombre,
            vApellidos: values.vApellidos,
            vTelefono: values.vTelefono || '',
            vDireccion: values.vDireccion || '',
            idRolPOS: selectedRol, // Rol obligatorio (05 Feb 2026)
            bIsSuperAdmin: 0,
            vMoneda: vMoneda,
            dTipoCambio: dTipoCambio,
            dPrecioUnitario: datosRenovacion.dPrecioUnitario,
            dSubTotal: datosRenovacion.dSubTotal,
            dIVA: datosRenovacion.dIVA,
            dTotal: datosRenovacion.dTotal
        };

        console.log('Payload preparado:', payload);

        // Si es suscripción (forma pago 99) y la licencia tiene suscripción activa
        if (esSuscripcion && infoSuscripcion) {
            console.log('FLUJO: Suscripción detectada, mostrando modal de confirmación');
            // Agregar datos de suscripción al payload
            payload.idSuscripcion = infoSuscripcion.idSuscripcion;

            // Guardar payload y mostrar modal de confirmación de pago
            setPayloadPendiente(payload);
            setShowModalConfirmacion(true);
        } else {
            console.log('FLUJO: Orden única (sin suscripción)');
            // Flujo normal: crear usuario con orden única
            await ejecutarCreacionUsuario(payload, false);
        }
    };

    // Ejecutar la creación del usuario según el flujo
    const ejecutarCreacionUsuario = async (payload, cobrarAhora = false) => {
        setSaving(true);
        try {
            // Determinar endpoint según si tiene suscripción o no
            const formaPagoSeleccionada = formasPago.find(f => f.idCatFormaPago === payload.idCatFormaPago);
            const esSuscripcion = formaPagoSeleccionada && String(formaPagoSeleccionada.vClave).trim() == '99';

            console.log('=== EJECUTAR CREACION ===');
            console.log('formaPagoSeleccionada:', formaPagoSeleccionada);
            console.log('esSuscripcion:', esSuscripcion);
            console.log('infoSuscripcion:', infoSuscripcion);
            console.log('cobrarAhora:', cobrarAhora);

            let endpoint;
            let payloadFinal = { ...payload };

            if (esSuscripcion && infoSuscripcion) {
                // Usuario con suscripción
                endpoint = '/api/UsuariosAkasiaPOS/CrearUsuarioConSuscripcion';
                payloadFinal.bCobrarAhora = cobrarAhora;
                console.log('ENDPOINT: CrearUsuarioConSuscripcion');
            } else {
                // Usuario con orden única
                endpoint = '/api/UsuariosAkasiaPOS/CrearUsuarioConOrdenUnica';
                console.log('ENDPOINT: CrearUsuarioConOrdenUnica');
            }

            console.log('Payload final a enviar:', payloadFinal);

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(JSON.stringify(payloadFinal))
            });

            if (!res.ok) {
                const errorText = await res.text();
                console.error('Error HTTP:', res.status, errorText);
                showErrorToast(`Error ${res.status}: ${errorText || res.statusText}`);
                return;
            }

            const data = await res.json();
            console.log('Respuesta del servidor:', data);

            if (data && data.iResponseType === 1) {
                showSuccessToast('Usuario POS creado correctamente.');
                onCreated && onCreated();
                onHide(); // Cerrar modal después de éxito
            } else {
                const mensajeError = data?.vMessage || data?.vCode || 'No se pudo crear el usuario';
                console.error('Error del SP:', mensajeError);
                showErrorToast(mensajeError);
            }
        } catch (err) {
            showErrorToast('Error al crear usuario: ' + err.message);
        } finally {
            setSaving(false);
            setPayloadPendiente(null);
        }
    };

    // Handler del modal de confirmación de pago
    const handleConfirmacionPago = async (cobrarAhora) => {
        setShowModalConfirmacion(false);
        if (payloadPendiente) {
            await ejecutarCreacionUsuario(payloadPendiente, cobrarAhora);
        }
    };

    return (
        <>
            <Modal show={show} onHide={onHide} size="lg" backdrop="static" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img src="/img/usuario20.png" alt="Usuario" className="me-2" style={{ width: '24px', height: '24px' }} />
                        Nuevo Usuario Akasia POS
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label>Licencia</Form.Label>
                                <Form.Control plaintext readOnly defaultValue={selectedLicense ? `${selectedLicense} ${licensesList && licensesList.length ? '- ' + (licensesList[0].vUUIDLicencia || '') : ''}` : ''} />
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Label>Sucursal <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    value={selectedSucursal || ''}
                                    onChange={(e) => setSelectedSucursal(parseInt(e.target.value))}
                                    disabled={saving || consultandoTC || cargandoSucursales}
                                >
                                    {cargandoSucursales ? (
                                        <option value="">Cargando sucursales...</option>
                                    ) : sucursales.length === 0 ? (
                                        <option value="">No hay sucursales disponibles</option>
                                    ) : (
                                        <>
                                            <option value="">Seleccione una sucursal</option>
                                            {sucursales.map(s => (
                                                <option key={s.idSucursal} value={s.idSucursal}>
                                                    {s.vNombreSucursal}
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    El usuario se creará en esta sucursal de Akasia POS
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Label>Rol <span className="text-danger">*</span></Form.Label>
                                <Form.Select
                                    value={selectedRol || ''}
                                    onChange={(e) => setSelectedRol(parseInt(e.target.value))}
                                    disabled={saving || consultandoTC || cargandoRoles}
                                >
                                    {cargandoRoles ? (
                                        <option value="">Cargando roles...</option>
                                    ) : roles.length === 0 ? (
                                        <option value="">No hay roles disponibles</option>
                                    ) : (
                                        <>
                                            <option value="">Seleccione un rol</option>
                                            {roles.map(r => (
                                                <option key={r.iRolId} value={r.iRolId}>
                                                    {r.vRolName}
                                                </option>
                                            ))}
                                        </>
                                    )}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Define los permisos y accesos del usuario en el sistema
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    name="vNombre"
                                    type="text"
                                    value={values.vNombre}
                                    onChange={handleChange}
                                    placeholder="Nombre(s)"
                                    disabled={saving || consultandoTC}
                                />
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Label>Apellidos <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    name="vApellidos"
                                    type="text"
                                    value={values.vApellidos}
                                    onChange={handleChange}
                                    placeholder="Apellidos"
                                    disabled={saving || consultandoTC}
                                />
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Label>Correo <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    name="vEmail"
                                    type="email"
                                    value={values.vEmail}
                                    onChange={handleChange}
                                    placeholder="correo@ejemplo.com"
                                    disabled={saving || consultandoTC}
                                />
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Row>
                                    <Col>
                                        <Form.Label>Usuario <span className="text-danger">*</span></Form.Label>
                                    </Col>
                                    <Col xs="auto">
                                        <IconButton
                                            iconClassName="fs--2 me-1"
                                            variant="falcon-primary"
                                            size="sm"
                                            icon="circle"
                                            onClick={() => generarCadenaRandom('vUsuario')}
                                            disabled={saving || consultandoTC}
                                        >
                                            Generar
                                        </IconButton>
                                    </Col>
                                </Row>
                                <Form.Control
                                    name="vUsuario"
                                    value={values.vUsuario}
                                    onChange={handleChange}
                                    placeholder="Nombre de usuario"
                                    disabled={saving || consultandoTC}
                                    autoComplete="off"
                                    onKeyUp={(event) => { event.target.value = event.target.value.toUpperCase(); }}
                                />
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Row>
                                    <Col>
                                        <Form.Label>Contraseña <span className="text-danger">*</span></Form.Label>
                                    </Col>
                                    <Col xs="auto">
                                        <IconButton
                                            iconClassName="fs--2 me-1"
                                            variant="falcon-primary"
                                            size="sm"
                                            icon="circle"
                                            onClick={() => generarCadenaRandom('vContrasena')}
                                            disabled={saving || consultandoTC}
                                        >
                                            Generar
                                        </IconButton>
                                    </Col>
                                </Row>
                                <Form.Control
                                    name="vContrasena"
                                    type="text"
                                    value={values.vContrasena}
                                    onChange={handleChange}
                                    placeholder="Contraseña"
                                    disabled={saving || consultandoTC}
                                    autoComplete="new-password"
                                />
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Label>Teléfono</Form.Label>
                                <Form.Control
                                    name="vTelefono"
                                    type="text"
                                    value={values.vTelefono}
                                    onChange={handleChange}
                                    placeholder="Teléfono (opcional)"
                                    disabled={saving || consultandoTC}
                                />
                            </Form.Group>

                            <Form.Group className="mb-2">
                                <Form.Label>Dirección</Form.Label>
                                <Form.Control
                                    name="vDireccion"
                                    as="textarea"
                                    rows={2}
                                    value={values.vDireccion}
                                    onChange={handleChange}
                                    placeholder="Dirección (opcional)"
                                    disabled={saving || consultandoTC}
                                />
                            </Form.Group>

                            <hr />

                            {/* Selector de Forma de Pago */}
                            <Form.Group className="mb-2">
                                <Form.Label>
                                    <FontAwesomeIcon icon="credit-card" className="me-2" />
                                    Forma de Pago <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                    value={selectedFormaPago || ''}
                                    onChange={(e) => setSelectedFormaPago(Number.parseInt(e.target.value))}
                                    disabled={saving || consultandoTC}
                                >
                                    <option value="">Seleccione forma de pago...</option>
                                    {formasPago.map(fp => (
                                        <option key={fp.idCatFormaPago} value={fp.idCatFormaPago}>
                                            {fp.vClave} - {fp.vDescripcionFormaPago}
                                        </option>
                                    ))}
                                </Form.Select>
                                {infoSuscripcion && (
                                    <Form.Text className="text-muted">
                                        💡 "99 - POR DEFINIR" agregará el usuario a tu suscripción existente
                                    </Form.Text>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <h6 className="fw-bold">Productos</h6>
                            <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                                {/* Productos de la base de datos (filtrados por suscripción si aplica) */}
                                {productosFiltrados.length === 0 && productos.length === 0 ? (
                                    <div className="text-muted small">No hay productos disponibles</div>
                                ) : productosFiltrados.length === 0 && infoSuscripcion ? (
                                    <div className="text-muted small">No hay productos compatibles con la suscripción {infoSuscripcion.vTipoRenovacion}</div>
                                ) : (
                                    (productosFiltrados.length > 0 ? productosFiltrados : productos).map(p => (
                                        <div key={p.idProducto} className={`p-2 rounded-2 mb-2 ${productoSeleccionado?.idProducto === p.idProducto ? 'bg-primary bg-opacity-10 border border-primary border-opacity-25' : 'border'}`} style={{ cursor: 'pointer' }} onClick={() => onSelectProducto(p)}>
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1">
                                                    <div className="fw-600">{p.vNombreProducto}</div>
                                                    <div className="small text-muted">{p.vCodigoBarras}</div>
                                                </div>
                                                <span className={`badge ${p.vMoneda === 'USD' ? 'bg-primary' : 'bg-success'}`}>{p.vMoneda}</span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            <hr />

                            {/* Configuración de Precios - Disponible para todos los productos */}
                            {productoSeleccionado && (
                                <>
                                    <h6 className="fw-bold">Configuración de Precios</h6>

                                    {/* Selector de Moneda */}
                                    <Form.Group className="mb-2">
                                        <Form.Label>
                                            <FontAwesomeIcon icon="dollar-sign" className="me-2" />
                                            Moneda *
                                        </Form.Label>
                                        <Form.Select
                                            value={vMoneda}
                                            onChange={(e) => {
                                                setVMoneda(e.target.value);
                                                // Si cambia a USD, obtener tipo de cambio
                                                if (e.target.value === 'USD') {
                                                    obtenerBanxicoTC();
                                                } else {
                                                    setDTipoCambio(1.0);
                                                }
                                            }}
                                            disabled={saving || consultandoTC}
                                        >
                                            <option value="MXN">MXN - Pesos Mexicanos</option>
                                            <option value="USD">USD - Dólares</option>
                                        </Form.Select>
                                        {vMoneda === 'USD' && dTipoCambio > 1 && (
                                            <Form.Text className="text-success">
                                                💱 Tipo de cambio: $1 USD = ${dTipoCambio.toFixed(4)} MXN
                                            </Form.Text>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-2">
                                        <Form.Label>Tipo de Renovación</Form.Label>
                                        <Form.Select
                                            value={tipoRenovacionSeleccionado}
                                            onChange={(e) => setTipoRenovacionSeleccionado(e.target.value)}
                                            disabled={saving || consultandoTC}
                                        >
                                            <option value="">Seleccione...</option>
                                            {/* Mostrar renovaciones disponibles para el producto seleccionado */}
                                            {(renovacionesFiltradas.length > 0 ? renovacionesFiltradas : renovaciones).map(r => (
                                                <option key={r.idProductoRenovaciones || r.idProductoRenovacion} value={r.vTipoRenovacion || r.vNombreRenovacion}>
                                                    {r.vTipoRenovacion || r.vNombreRenovacion} - ${r.dTotal}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-2">
                                        <Form.Label>Precio Total (con IVA)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={precioTotal}
                                            onChange={(e) => {
                                                setPrecioTotal(e.target.value);
                                                recalcularPrecios(e.target.value);
                                            }}
                                            placeholder="0.00"
                                            disabled={saving || consultandoTC}
                                        />
                                    </Form.Group>

                                    {precioTotal && parseFloat(precioTotal) > 0 && (
                                        <div className="bg-light p-2 rounded mb-2">
                                            <div className="small">
                                                <div className="d-flex justify-content-between">
                                                    <span>Precio Unitario:</span>
                                                    <span className="fw-bold">${precioUnitario.toFixed(2)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between">
                                                    <span>IVA (16%):</span>
                                                    <span className="fw-bold">${ivaCalculado.toFixed(2)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between border-top pt-1 mt-1">
                                                    <span className="fw-bold">Total:</span>
                                                    <span className="fw-bold text-success">${parseFloat(precioTotal).toFixed(2)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}



                            {/* Mostrar información de moneda y tipo de cambio */}
                            {vMoneda && (
                                <div className="mb-2">
                                    <div className="small text-muted">
                                        <span className="fw-bold">Moneda:</span> {vMoneda}
                                        {vMoneda === 'USD' && (
                                            <>
                                                <br />
                                                <span className="fw-bold">Tipo de Cambio:</span> ${dTipoCambio.toFixed(4)}
                                                {consultandoTC && <Spinner animation="border" size="sm" className="ms-2" />}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}


                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={saving || consultandoTC}>Cerrar</Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving || consultandoTC}>
                        {(saving || consultandoTC) ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                {consultandoTC ? 'Consultando tipo de cambio...' : 'Creando...'}
                            </>
                        ) : 'Crear Usuario'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de confirmación de pago para suscripciones */}
            <ModalConfirmacionPagoSuscripcion
                show={showModalConfirmacion}
                onHide={() => {
                    setShowModalConfirmacion(false);
                    setPayloadPendiente(null);
                }}
                onConfirm={handleConfirmacionPago}
                diferencia={payloadPendiente?.dTotal || 0}
                moneda={vMoneda}
            />
        </>
    );
};

export default ModalNuevoUsuarioAkasiaPOS;

ModalNuevoUsuarioAkasiaPOS.propTypes = {
    show: PropTypes.bool,
    onHide: PropTypes.func,
    idLicencia: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    licencias: PropTypes.shape({
        idClienteAkasia: PropTypes.number,
        idLicencia: PropTypes.number,
        vUUIDLicencia: PropTypes.string,
        vUUIDEmpresa: PropTypes.string
    }),
    onCreated: PropTypes.func,
    infoSuscripcionProp: PropTypes.shape({
        idSuscripcion: PropTypes.number,
        iStatus: PropTypes.number,
        vStripeSubscriptionId: PropTypes.string,
        vStripeProductId: PropTypes.string,
        vStripePriceId: PropTypes.string,
        vTipoRenovacion: PropTypes.string,
        idCatFormaPago: PropTypes.number
    })
};
